/**
 * Widget Default Configurations Index
 *
 * Exports all widget type default configuration factories.
 */

// Import factories for the factory map (must be at top for ESLint)
import { createButtonConfig } from './button.config';
import { createContentBlockConfig } from './contentBlock.config';
import { createDividerConfig } from './divider.config';
import { createYourBuildConfig } from './yourBuild.config';
import { createBuildConfig } from './build.config';

// Button Widget
export {
  createButtonConfig,
  BUTTON_DEFAULT_CONFIG,
  default as createButtonConfigDefault,
  type ButtonWidgetConfig,
  type ButtonStyle,
  type ButtonSize,
} from './button.config';

// Content Block Widget
export {
  createContentBlockConfig,
  CONTENT_BLOCK_DEFAULT_CONFIG,
  default as createContentBlockConfigDefault,
  type ContentBlockWidgetConfig,
} from './contentBlock.config';

// Divider Widget
export {
  createDividerConfig,
  DIVIDER_DEFAULT_CONFIG,
  default as createDividerConfigDefault,
  type DividerWidgetConfig,
  type DividerOrientation,
  type DividerStyle,
} from './divider.config';

// Your Build Widget
export {
  createYourBuildConfig,
  mergeWithDefaults as mergeYourBuildWithDefaults,
  YOUR_BUILD_DEFAULT_CONFIG,
  default as createYourBuildConfigDefault,
  type YourBuildWidgetConfig,
  type ButtonConfig,
  type AddToCartButtonConfig,
} from './yourBuild.config';

// Build Widget
export {
  createBuildConfig,
  BUILD_DEFAULT_CONFIG,
  default as createBuildConfigDefault,
  type BuildWidgetConfig,
} from './build.config';

// ============================================================================
// Factory Map
// ============================================================================

// Type alias for widget config factory functions
type WidgetConfigFactory = () => Record<string, unknown>;

/**
 * Map of widget type IDs to their config factory functions
 */
export const widgetConfigFactories: Record<string, WidgetConfigFactory> = {
  button: createButtonConfig as unknown as WidgetConfigFactory,
  contentBlock: createContentBlockConfig as unknown as WidgetConfigFactory,
  divider: createDividerConfig as unknown as WidgetConfigFactory,
  yourBuild: createYourBuildConfig as unknown as WidgetConfigFactory,
  build: createBuildConfig as unknown as WidgetConfigFactory,
};

/**
 * Get the default configuration factory for a widget type
 * @param widgetType The widget type ID
 * @returns The configuration factory function or undefined
 */
export const getWidgetConfigFactory = (
  widgetType: string
): (() => Record<string, unknown>) | undefined => {
  return widgetConfigFactories[widgetType];
};

/**
 * Create default configuration for any widget type
 * @param widgetType The widget type ID
 * @returns The default configuration or an empty object if type not found
 */
export const createWidgetConfig = (widgetType: string): Record<string, unknown> => {
  const factory = widgetConfigFactories[widgetType];
  if (factory) {
    return factory();
  }
  // Fallback for unknown widget types
  return {};
};
